import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { approvalsApi } from '@/api/approvals.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useAccessibleApprovalModules } from '@/hooks/useAccessibleApprovalModules';
import type { ApprovalMatrixRow } from '@/types/approvals';
import { showApiError } from '@/utils/apiError';

export function ApprovalMatrixPage() {
  const { modules } = useAccessibleApprovalModules({ includeAll: false });
  const [rows, setRows] = useState<ApprovalMatrixRow[]>([]);
  const [loading, setLoading] = useState(false);

  const accessibleModuleCodes = useMemo(
    () => new Set(modules.map((m) => m.value)),
    [modules],
  );
  const visibleRows = useMemo(
    () => rows.filter((row) => accessibleModuleCodes.has(row.ownerModule)),
    [rows, accessibleModuleCodes],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await approvalsApi.matrix());
    } catch (err) {
      showApiError(err, 'Failed to load approval matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<ApprovalMatrixRow>[] = [
    { key: 'ownerModule', header: 'Module' },
    { key: 'entityAction', header: 'Action' },
    { key: 'workflowName', header: 'Workflow' },
    { key: 'approvalType', header: 'Type' },
    {
      key: 'conditionSummary',
      header: 'Conditions',
      render: (row) => row.conditionSummary.join(' · ') || '—',
    },
    {
      key: 'approverSummary',
      header: 'Approvers',
      render: (row) => row.approverSummary.join(', ') || '—',
    },
    {
      key: 'slaHours',
      header: 'SLA',
      render: (row) => `${row.slaHours}h`,
    },
    { key: 'escalationSummary', header: 'Escalation' },
    {
      key: 'active',
      header: 'Status',
      render: (row) => (row.active ? 'Active' : 'Inactive'),
    },
  ];

  return (
    <div className="page-content">
      <PageHeader
        title="Approval Matrix"
        description="Who approves what — module, action, conditions, approvers, SLA, and escalation at a glance"
        breadcrumbs={[
          { label: 'Settings', to: '/work' },
          { label: 'Approval Matrix' },
        ]}
        actions={
          <Link to="/approvals/builder">
            <Button variant="secondary">Open Builder</Button>
          </Link>
        }
      />

      <DataTable
        columns={columns}
        data={visibleRows}
        keyField="workflowName"
        loading={loading}
        emptyMessage="No approval workflows configured. Create workflows in the Approval Builder."
      />
    </div>
  );
}
